/**
 * Canonical public URL paths for the 7 self-managed detail page types and the
 * contract for ISR cache invalidation.
 *
 * SINGLE SOURCE OF TRUTH for the detail-route URL scheme. The AERO web app's
 * `matchDetailRoute` / `buildXMeta` (artifacts/aero/src/lib/seo/meta.ts) and the
 * api-server's ISR-invalidation calls (artifacts/api-server/src/lib/isrInvalidate.ts)
 * both build paths through these helpers, so the cache key the web server stores
 * (`${origin}\u0000${canonicalPath}`) can never drift from the path the api-server
 * asks it to purge after a mutation.
 *
 * These are root-relative paths (no origin, no base-path prefix); the base path
 * is stripped from the cache key on the web side, so callers pass the bare
 * canonical path.
 */

/** `/airports/<code>` — code is the stored (uppercase) ICAO/identifier. */
export function airportDetailPath(code: string): string {
  return `/airports/${code}`;
}

/** `/fbos/<airportCode>/<nameSlug>`. */
export function fboDetailPath(airportCode: string, nameSlug: string): string {
  return `/fbos/${airportCode}/${nameSlug}`;
}

/** `/flight-schools/<airportCode>/<nameSlug>`. */
export function flightSchoolDetailPath(
  airportCode: string,
  nameSlug: string,
): string {
  return `/flight-schools/${airportCode}/${nameSlug}`;
}

/** `/maintenance/<airportCode>/<nameSlug>`. */
export function maintenanceDetailPath(
  airportCode: string,
  nameSlug: string,
): string {
  return `/maintenance/${airportCode}/${nameSlug}`;
}

/** `/services/<slug>` — the generic service-provider detail page. */
export function providerDetailPath(slug: string): string {
  return `/services/${slug}`;
}

/** `/experiences/<slug>` — the experience/event detail page. */
export function experienceDetailPath(slug: string): string {
  return `/experiences/${slug}`;
}

/** `/p/<slug>` — an ordinary CMS detail page. */
export function cmsPageDetailPath(slug: string): string {
  return `/p/${slug}`;
}

// ---------------------------------------------------------------------------
// CMS detail templates → detail-path prefixes.
// ---------------------------------------------------------------------------

/**
 * The reserved `template-*` CMS page slugs and the canonical path PREFIX of
 * the detail pages each template drives. Publishing/unpublishing one of these
 * templates changes the rendering of EVERY detail page of that entity type,
 * so the ISR purge must cover the whole prefix, not just the template page's
 * own `/p/<slug>` path.
 *
 * Keep in lockstep with the per-entity path builders above.
 */
export const CMS_TEMPLATE_DETAIL_PREFIXES: Readonly<Record<string, string>> = {
  "template-fbo": "/fbos/",
  "template-airport": "/airports/",
  "template-flight_school": "/flight-schools/",
  "template-maintenance": "/maintenance/",
  "template-services": "/services/",
  "template-experience": "/experiences/",
};

/**
 * Detail-path prefix affected by publishing the CMS page with this slug, or
 * `undefined` for ordinary (non-template) pages.
 */
export function cmsTemplateDetailPrefix(slug: string): string | undefined {
  return CMS_TEMPLATE_DETAIL_PREFIXES[slug];
}

// ---------------------------------------------------------------------------
// Universal / app links (mobile deep-linking into shared listing URLs).
// ---------------------------------------------------------------------------

/**
 * Canonical-path PREFIXES for the listing detail pages the mobile app can
 * open directly via iOS Universal Links / Android App Links. Consumed by the
 * api-server's `/.well-known/apple-app-site-association` and
 * `/.well-known/assetlinks.json` responses.
 *
 * Keep in lockstep with:
 * - the per-entity path builders above,
 * - the mobile resolver routes in `artifacts/aero-mobile/app`
 *   (`fbos/[airportCode]/[nameSlug]`, `airports/[code]`,
 *   `flight-schools/[airportCode]/[nameSlug]`,
 *   `maintenance/[airportCode]/[nameSlug]`, `services/[slug]`),
 * - `APP_LINK_PATH_PREFIXES` in `artifacts/aero-mobile/app.config.js`
 *   (static config — cannot import this package).
 *
 * A prefix listed here but lacking a mobile route sends pilots to a dead
 * in-app screen; a mobile route missing here keeps opening in the browser.
 */
export const UNIVERSAL_LINK_PATH_PREFIXES: readonly string[] = [
  "/fbos/",
  "/airports/",
  "/flight-schools/",
  "/maintenance/",
  "/services/",
];

// ---------------------------------------------------------------------------
// ISR invalidation contract (shared between the api-server caller and the web
// server's purge endpoint).
// ---------------------------------------------------------------------------

/** Path (under the web app's base path) of the ISR purge endpoint. */
export const ISR_INVALIDATE_PATH = "/__isr/invalidate";

/** Header carrying the shared secret that authorizes a purge request. */
export const ISR_TOKEN_HEADER = "x-isr-token";

/**
 * Path (under the web app's base path) of the read-only render-cache stats
 * endpoint. Authorized by the same {@link ISR_TOKEN_HEADER} shared secret as
 * the purge endpoint; returns hit/miss/eviction counters plus the active
 * TTL/stale/max-entries config so operators can tune the cache with data.
 */
export const ISR_STATS_PATH = "/__isr/stats";

/**
 * Request body for {@link ISR_INVALIDATE_PATH}: the canonical paths to purge,
 * plus optional path PREFIXES (e.g. `"/services/"`) that purge every cached
 * entry whose pathname starts with the prefix — used when a CMS detail
 * template is published and every detail page of that entity type is affected.
 */
export interface IsrInvalidateBody {
  paths: string[];
  prefixes?: string[];
}
